// Google Analytics 4 утилита для Main-Bet
// Замените 'G-XXXXXXXXXX' на ваш реальный Measurement ID

const GA_MEASUREMENT_ID = 'G-4Y31KJ8FGC'; // Замените на ваш ID

// Инициализация Google Analytics
export const initGA = () => {
  // Проверяем, что мы в браузере и GA еще не загружен
  if (typeof window === 'undefined' || window.gtag) {
    return;
  }

  // Создаем скрипт для загрузки GA
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Инициализируем gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Конфигурируем GA
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
    // Отключаем автоматическое отслеживание страниц для SPA
    send_page_view: false
  });

  console.log('Google Analytics initialized');
};

// Отслеживание просмотров страниц
export const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });

  console.log('GA: Page view tracked:', path);
};

const TITLE_FIRST_WAIT_MS = 1500;
const TITLE_SETTLE_MS = 500;
const TITLE_MAX_WAIT_MS = 4000;

/**
 * Lazy pages set their <title> via Helmet only after the chunk and data load,
 * so an immediate page_view would carry the previous page's title.
 * Sends once the title stops changing; returns a flush for route changes.
 */
export const trackPageViewWhenTitleSettles = (path) => {
  if (typeof window === 'undefined') return () => {};

  let sent = false;
  let settleTimer = null;
  let maxTimer = null;
  let observer = null;

  const send = () => {
    if (sent) return;
    sent = true;
    clearTimeout(settleTimer);
    clearTimeout(maxTimer);
    if (observer) observer.disconnect();
    trackPageView(path, document.title);
  };

  const schedule = (delay) => {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(send, delay);
  };

  const titleEl = document.querySelector('title');
  if (titleEl && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => schedule(TITLE_SETTLE_MS));
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
  }

  schedule(TITLE_FIRST_WAIT_MS);
  maxTimer = setTimeout(send, TITLE_MAX_WAIT_MS);

  return send;
};

// Отслеживание времени на сайте (для новых посетителей)
export const trackNewVisitor = () => {
  // Проверяем, первый ли это визит
  let isFirstVisit = false;

  try {
    isFirstVisit = !localStorage.getItem('main_bet_visited');
    if (isFirstVisit) {
      localStorage.setItem('main_bet_visited', 'true');
    }
  } catch (error) {
    return;
  }

  if (isFirstVisit) {
    // Отслеживаем через 15 секунд (как в оригинальном коде)
    setTimeout(() => {
      if (window.gtag) {
        window.gtag('event', 'new_visitor', {
          event_category: 'user_behavior',
          event_label: window.location.pathname
        });
      }
    }, 15000);
  }
};

// Отслеживание событий
export const trackEvent = (eventName, category = 'button_click') => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', eventName, {
    event_category: category,
    transport_type: 'beacon'
  });
};

// Subscribe click to one of our channels (telegram, whatsapp, instagram, x) and where it was
export const trackChannelClick = (channel, placement) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'channel_click', {
    channel,
    placement,
    transport_type: 'beacon'
  });
};

// Отслеживание просмотра прогнозов с дополнительными данными
export const trackPredictionView = (prediction) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'prediction_view', {
    event_category: 'content',
    event_label: `${prediction.homeTeam} vs ${prediction.awayTeam}`,
    ...{
      confidence: prediction.confidence,
      prediction: prediction.prediction,
      match_date: prediction.matchDate,
      league: prediction.league || 'unknown'
    }
  });
};

// Отслеживание просмотра гайдов
export const trackGuideView = (guide) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'guide_view', {
    event_category: 'content',
    event_label: guide.title,
    ...{
      category: guide.category,
      author: guide.author,
      reading_time: Math.ceil(guide.content?.replace(/<[^>]*>/g, '').split(' ').length / 200)
    }
  });
};

// Отслеживание просмотра бонусов
export const trackBonusView = (bonus) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'bonus_view', {
    event_category: 'content',
    event_label: bonus.title,
    ...{
      bookmaker: bonus.bookmaker,
      amount: bonus.amount,
      wagering: bonus.wagering,
      bonus_type: bonus.type || 'welcome'
    }
  });
};

// Отслеживание переходов на букмекерские сайты
export const trackBookmakerClick = (bookmaker, source = 'bonus') => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'bookmaker_click', {
    event_category: 'outbound_link',
    event_label: bookmaker,
    bookmaker_source: source,
    transport_type: 'beacon'
  });
};

// Отслеживание использования промокодов
export const trackPromoCodeAction = (action, code, bookmaker) => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  window.gtag('event', 'promo_code_action', {
    event_category: 'engagement',
    event_label: bookmaker,
    ...{
      action: action, // 'view', 'copy', 'click'
      promo_code: code
    }
  });
};

const accaPayload = ({ profile, parsed, dateKey, source, method }) => ({
  event_category: 'acca',
  event_label: profile || 'unknown',
  profile: profile || 'unknown',
  legs: parsed?.picks?.length ?? 0,
  total_odds: Number.isFinite(parsed?.totalOdds)
    ? Number(parsed.totalOdds.toFixed(2))
    : undefined,
  date_key: dateKey || 'unknown',
  source: source || 'acca_page',
  ...(method ? { method } : {}),
  transport_type: 'beacon'
});

/** Bet CTA on a ready-made acca slip */
export const trackAccaBetClick = ({ profile, parsed, dateKey, source }) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'acca_bet_click', accaPayload({ profile, parsed, dateKey, source }));
};

/** Share / copy of a ready-made acca slip */
export const trackAccaShare = ({ profile, parsed, dateKey, source, method }) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag(
    'event',
    'acca_share',
    accaPayload({ profile, parsed, dateKey, source, method })
  );
};

/** Empty daily-acca list after feed load */
export const trackAccaEmpty = (source = 'acca_page') => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'acca_empty', {
    event_category: 'acca',
    event_label: source,
    source,
    transport_type: 'beacon'
  });
};

const analytics = {
  initGA,
  trackPageView,
  trackNewVisitor,
  trackEvent,
  trackPredictionView,
  trackGuideView,
  trackBonusView,
  trackBookmakerClick,
  trackPromoCodeAction,
  trackAccaBetClick,
  trackAccaShare,
  trackAccaEmpty
};

export default analytics; 